import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { useInView } from '../hooks/useInView';
import { propertiesApi } from '../services/api';
import PropertyCard from './PropertyCard';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from './ui/carousel';

const SoldSection = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carouselApi, setCarouselApi] = useState(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    if (!carouselApi) return;

    const updateScrollState = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    updateScrollState();
    carouselApi.on('select', updateScrollState);
    carouselApi.on('reInit', updateScrollState);

    return () => {
      carouselApi.off('select', updateScrollState);
    };
  }, [carouselApi]);

  const loadProperties = async () => {
    try {
      const data = await propertiesApi.getAll('vendu');
      setProperties(data);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollPrev = () => carouselApi?.scrollPrev();
  const scrollNext = () => carouselApi?.scrollNext();

  if (!loading && properties.length === 0) {
    return null;
  }

  return (
    <section ref={ref} className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-12 transition-all duration-400 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#8B2942] mb-4">Vendus</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Nos récentes ventes réussies
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B2942]"></div>
          </div>
        ) : (
          <div className="relative px-16 md:px-20">
            {/* Navigation Buttons */}
            <Button
              variant="outline"
              size="icon"
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full border-[#8B2942] text-[#8B2942] hover:bg-[#8B2942] hover:text-white transition-all duration-300 ${
                !canScrollPrev ? 'opacity-50 cursor-not-allowed' : 'shadow-lg'
              }`}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={scrollNext}
              disabled={!canScrollNext}
              className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full border-[#8B2942] text-[#8B2942] hover:bg-[#8B2942] hover:text-white transition-all duration-300 ${
                !canScrollNext ? 'opacity-50 cursor-not-allowed' : 'shadow-lg'
              }`}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>

            {/* Carousel */}
            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: 'start',
                loop: false,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-6">
                {properties.map((property, index) => (
                  <CarouselItem key={property.id} className="pl-6 md:basis-1/2 lg:basis-1/3">
                    <PropertyCard property={property} index={index} showStatus />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {/* View more button */}
            {properties.length > 3 && (
              <div className="text-center mt-10">
                <Button asChild variant="outline" size="lg" className="group border-[#8B2942] text-[#8B2942] hover:bg-[#8B2942] hover:text-white">
                  <Link to="/vendus" className="flex items-center gap-2">
                    Voir tous les biens vendus ({properties.length})
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default SoldSection;
